/**
 * Open register (S41) — count the opening float and start a cash-drawer shift.
 * @see prototype/pos/shift-open.html
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import { meQueryOptions } from "../../lib/session.ts";

const toPaise = (s: string) => String(Math.round((parseFloat(s.replace(/[^0-9.]/g, "")) || 0) * 100));

export function OpenRegisterPage({ onOpened }: { onOpened: () => void }) {
  const me = useQuery(meQueryOptions);
  const navigate = useNavigate();
  const [float, setFloat] = useState("2000");
  const open = trpc.pos.openShift.useMutation({
    onSuccess: () => {
      toast.success("Register opened — start selling");
      onOpened();
    },
    onError: (e) => toast.error("Couldn't open the register", { description: e.message }),
  });
  const add = (n: number) => setFloat((p) => String((parseInt(p.replace(/[^0-9]/g, ""), 10) || 0) + n));

  return (
    <div className="flex min-h-screen flex-col bg-bg text-ink">
      <header className="flex h-14 flex-shrink-0 items-center gap-3 border-b border-line bg-paper px-4">
        <div className="grid h-8 w-8 place-items-center rounded-md bg-ink text-sm font-extrabold text-white">F</div>
        <div className="leading-tight">
          <div className="text-sm font-bold">Flint POS</div>
          <div className="text-xs text-muted">{me.data?.company?.legalName ?? "Outlet"}</div>
        </div>
        <div className="flex-1" />
        <button
          onClick={() => navigate({ to: "/" })}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line bg-paper px-3 text-sm font-semibold text-ink-2 hover:bg-bg"
          title="Exit to workspace"
        >
          ← Exit
        </button>
      </header>

      <main className="mx-auto w-full max-w-lg px-4 pb-28 pt-8">
        <div className="text-xs font-bold uppercase tracking-[0.12em] text-sky-700">Start of day</div>
        <h1 className="mt-1.5 text-2xl font-bold tracking-tight">Open the register</h1>
        <p className="mt-1 text-sm text-muted">
          Count the cash in the drawer and confirm the opening float. This is the baseline the end-of-day
          Z-report reconciles against.
        </p>

        <div className="mt-5 rounded-xl border border-line bg-paper p-5 shadow-sm">
          <div className="mb-2 text-xs font-semibold text-ink-2">Counted cash in drawer</div>
          <div className="flex h-16 items-center gap-2 rounded-lg border-[1.5px] border-line-2 bg-paper px-4 focus-within:border-sky-600 focus-within:ring-4 focus-within:ring-sky-100">
            <span className="text-2xl font-bold text-muted">₹</span>
            <input
              value={float}
              onChange={(e) => setFloat(e.target.value)}
              inputMode="decimal"
              className="w-full border-0 bg-transparent font-mono text-3xl font-bold tracking-tight text-ink outline-none"
            />
          </div>
          <div className="mt-3 grid grid-cols-4 gap-2">
            {[500, 1000, 2000].map((n) => (
              <button
                key={n}
                onClick={() => add(n)}
                className="h-11 rounded-md border border-line bg-raised font-mono text-sm font-bold text-ink-2 hover:border-sky-600 hover:bg-sky-50 hover:text-sky-700"
              >
                +₹{n.toLocaleString("en-IN")}
              </button>
            ))}
            <button
              onClick={() => setFloat("0")}
              className="h-11 rounded-md border border-line bg-raised text-sm font-bold text-ink-2 hover:bg-bg"
            >
              Clear
            </button>
          </div>
        </div>
      </main>

      <div className="fixed inset-x-0 bottom-0 border-t border-line bg-paper px-4 py-3.5">
        <div className="mx-auto max-w-lg">
          <button
            disabled={open.isPending}
            onClick={() => open.mutate({ openingFloatPaise: toPaise(float) })}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-lg bg-ink text-base font-bold text-white shadow-md hover:bg-ink-2 disabled:opacity-50"
          >
            {open.isPending ? "Opening…" : "Open register & start selling"}
          </button>
        </div>
      </div>
    </div>
  );
}
